/* ⚠️ Charl, 16 Oct 2026, 20:43: "for the aluminium drawing canvas, I think you need 3D drawing capability. It's not the
   same as flashings." He chose FREE 3D MODELLING: draw faces in 3D, join, fold, weld, orbit, measure.

   The sheet-metal model behind the 3D workspace - plain numbers, no screen, no three.js - so the SAME file is used by the
   screen (client/src/pages/alu3d/Alu3DStudio.js) and by the server (server/alu-3d.js prices exactly what was drawn).
   Written as UMD (no import/export) so Node can require it and the app can import it.

   A MODEL is parts placed in 3D (millimetres, Y up). A PART is one flat blank, folded:
     - a base plate L (along its x) × W (along its z), lying flat
     - flanges folded off any free edge of any face: width, angle (0 flat .. 180 a hem), up or down
     - its two ends square, or mitred for a corner (a picture frame corner, any angle, either way)
     - a quantity, a position and a turn about the vertical
   Parts made by "Mitred corner" share their profile (a group): a flange added to one is added to all.
   WELDS are found, not typed: wherever two free edges of different faces meet along a line (a mitre, a tray corner, two
   parts placed edge to edge) there is a weld of that length. Any weld can be switched off.
   PRICING (server/alu-3d.js): each part is unfolded to its flat blank; the blank's length and width, its folds and the welds
   go into the aluminium engine like any other job. */
(function (root, factory) { if (typeof module === "object" && module.exports) module.exports = factory(); else root.Alu3DModel = factory(); })(this, function () {
  "use strict";
  const EPS = 0.5;                       /* mm - edges closer than this are the same line */
  const num = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };
  const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
  const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const mul = (a, k) => [a[0] * k, a[1] * k, a[2] * k];
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const len = a => Math.sqrt(dot(a, a));
  const unit = a => { const l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
  const rad = d => num(d) * Math.PI / 180;
  /* turn about the vertical (Y), degrees: (x, y, z) -> (x cos + z sin, y, -x sin + z cos) */
  const rotY = (p, deg) => { const t = rad(deg), c = Math.cos(t), s = Math.sin(t); return [p[0] * c + p[2] * s, p[1], -p[0] * s + p[2] * c]; };
  const clean = v => (Math.abs(v) < 1e-9 ? 0 : +v.toFixed(6));
  const cleanP = p => p.map(clean);

  let seq = 0;
  const uid = pre => (pre || "f") + Date.now().toString(36).slice(-4) + (++seq).toString(36);

  function newPart(o) {
    o = o || {};
    return { id: o.id || uid("p"), name: o.name || "Part", group: o.group || null, L: num(o.L) || 1000, W: num(o.W) || 100, qty: Math.max(1, Math.round(num(o.qty) || 1)),
      pos: o.pos || [0, 0, 0], rotY: num(o.rotY), flanges: (o.flanges || []).map(f => Object.assign({}, f)),
      ends: { start: Object.assign({ turn: 0, side: 1 }, o.ends && o.ends.start), end: Object.assign({ turn: 0, side: 1 }, o.ends && o.ends.end) } };
  }
  function newModel() { return { v: 1, parts: [newPart({ name: "Part 1", L: 1000, W: 100 })], weldsOff: [] }; }

  /* ---------- the faces of a part, in the part's own frame ----------
     a face is a rectangle: origin O, u along its first edge (length a), v across (b), up = the sheet's top side.
     edges: 0 (0,0)->(a,0) · 1 (a,0)->(a,b) · 2 (a,b)->(0,b) · 3 (0,b)->(0,0). A flange's edge 0 is its fold. */
  const EDGE = [
    { p: (a, b) => [0, 0], q: (a, b) => [a, 0], out: F => mul(F.v, -1) },
    { p: (a, b) => [a, 0], q: (a, b) => [a, b], out: F => F.u },
    { p: (a, b) => [a, b], q: (a, b) => [0, b], out: F => F.v },
    { p: (a, b) => [0, b], q: (a, b) => [0, 0], out: F => mul(F.u, -1) },
  ];
  const at = (F, uv) => add(F.O, add(mul(F.u, uv[0]), mul(F.v, uv[1])));
  function rawFaces(part, flat) {
    const base = { key: "base", O: [0, 0, 0], u: [1, 0, 0], v: [0, 0, 1], up: [0, 1, 0], a: Math.max(1, num(part.L)), b: Math.max(1, num(part.W)), parent: null, edge: null, depth: 0 };
    const out = [base], byKey = { base };
    let guard = 0, added = true;
    const left = part.flanges.slice();
    while (added && guard++ < 200) {          /* flanges may be listed in any order - attach each once its parent exists */
      added = false;
      for (let i = 0; i < left.length; i++) {
        const f = left[i], F = byKey[f.on || "base"];
        if (!F) continue;
        const E = EDGE[Math.max(0, Math.min(3, Math.round(num(f.edge))))];
        const P = at(F, E.p(F.a, F.b)), Q = at(F, E.q(F.a, F.b)), d = E.out(F);
        const th = flat ? 0 : rad(Math.max(0, Math.min(180, num(f.angle)))), s = num(f.dir) === -1 ? -1 : 1;
        const v = add(mul(d, Math.cos(th)), mul(F.up, s * Math.sin(th)));
        const up = sub(mul(F.up, Math.cos(th)), mul(d, s * Math.sin(th)));
        const face = { key: f.id, O: P, u: unit(sub(Q, P)), v: unit(v), up: unit(up), a: len(sub(Q, P)), b: Math.max(1, num(f.width)), parent: F.key, edge: Math.round(num(f.edge)), depth: F.depth + 1, flange: f };
        out.push(face); byKey[f.id] = face; left.splice(i, 1); i--; added = true;
      }
    }
    return out;
  }
  /* an end is cut square, or mitred for a corner: the outer edge of the turn keeps the full length */
  function mitreCut(part, faces) {
    const along = faces.filter(F => Math.abs(Math.abs(F.u[0]) - 1) < 1e-6);
    const zs = [];
    along.forEach(F => [[0, 0], [F.a, 0], [F.a, F.b], [0, F.b]].forEach(uv => zs.push(at(F, uv)[2])));
    const zmin = zs.length ? Math.min(...zs) : 0, zmax = zs.length ? Math.max(...zs) : 0;
    const off = (e, z) => { const t = num(e.turn); if (!(t > 0)) return 0; const k = Math.tan(rad(Math.min(179, t)) / 2); return k * (num(e.side) === -1 ? (zmax - z) : (z - zmin)); };
    const L = num(part.L);
    return { zmin, zmax, xmin: z => off(part.ends.start, z), xmax: z => L - off(part.ends.end, z), along: F => Math.abs(Math.abs(F.u[0]) - 1) < 1e-6 };
  }
  /* each face's four corners, in the part's frame, with the ends cut */
  function faces(part, opts) {
    const flat = !!(opts && opts.flat);
    const F = rawFaces(part, flat);
    const cut = flat ? null : mitreCut(part, F);
    return F.map(f => {
      let c = [[0, 0], [f.a, 0], [f.a, f.b], [0, f.b]].map(uv => at(f, uv));
      if (cut && cut.along(f)) {
        const xs = c.map(p => p[0]), lo = Math.min(...xs), hi = Math.max(...xs);
        c = c.map(p => { if (Math.abs(p[0] - lo) < 1e-6) return [cut.xmin(p[2]), p[1], p[2]]; if (Math.abs(p[0] - hi) < 1e-6) return [cut.xmax(p[2]), p[1], p[2]]; return p; });
      }
      return Object.assign({}, f, { corners: c.map(cleanP) });
    });
  }
  const toWorld = (part, p) => add(rotY(p, part.rotY), part.pos || [0, 0, 0]);
  /* every face of every part in the room, with its edges and whether each edge is a fold */
  function worldFaces(model) {
    const out = [];
    (model.parts || []).forEach(part => {
      const fs = faces(part);
      const used = {}; fs.forEach(f => { if (f.parent) used[f.parent + ":" + f.edge] = true; });
      fs.forEach(f => {
        const w = f.corners.map(p => cleanP(toWorld(part, p)));
        out.push({ part: part.id, key: f.key, corners: w, normal: unit(rotY(cross(f.u, f.v), part.rotY)),
          edges: [0, 1, 2, 3].map(i => ({ i, p: w[i], q: w[(i + 1) % 4], fold: (i === 0 && !!f.parent) || !!used[f.key + ":" + i] })) });
      });
    });
    return out;
  }

  /* ---------- welds: free edges of different faces on the same line ---------- */
  function overlapOf(e1, e2) {
    const d = sub(e1.q, e1.p), L1 = len(d); if (L1 < 1e-6) return 0;
    const u = mul(d, 1 / L1), d2 = unit(sub(e2.q, e2.p));
    if (len(cross(u, d2)) > 0.01) return 0;                       /* not parallel */
    const offLine = p => len(cross(sub(p, e1.p), u));
    if (offLine(e2.p) > EPS || offLine(e2.q) > EPS) return 0;     /* not on the same line */
    const t1 = dot(sub(e2.p, e1.p), u), t2 = dot(sub(e2.q, e1.p), u);
    const lo = Math.max(0, Math.min(t1, t2)), hi = Math.min(L1, Math.max(t1, t2));
    return hi - lo > 1 ? { len: hi - lo, p: add(e1.p, mul(u, lo)), q: add(e1.p, mul(u, hi)) } : 0;
  }
  function welds(model) {
    const W = worldFaces(model), byPart = {}; (model.parts || []).forEach(p => { byPart[p.id] = p; });
    const out = [], off = new Set(model.weldsOff || []);
    for (let i = 0; i < W.length; i++) for (let j = i + 1; j < W.length; j++) {
      const A = W[i], B = W[j];
      /* a hem lies on its own parent - faces of one part lying on each other are not a joint */
      if (A.part === B.part && len(cross(A.normal, B.normal)) < 0.02) continue;
      A.edges.forEach(ea => { if (ea.fold) return; B.edges.forEach(eb => { if (eb.fold) return;
        const o = overlapOf(ea, eb); if (!o) return;
        const id = [A.part + "/" + A.key + "/" + ea.i, B.part + "/" + B.key + "/" + eb.i].sort().join("|");
        const qty = Math.min(byPart[A.part].qty || 1, byPart[B.part].qty || 1);
        out.push({ id, len: +o.len.toFixed(1), exact: o.len, p: cleanP(o.p), q: cleanP(o.q), qty, on: !off.has(id), a: { part: A.part, face: A.key, edge: ea.i }, b: { part: B.part, face: B.key, edge: eb.i } });
      }); });
    }
    return out;
  }

  /* ---------- the flat blank ---------- */
  function blank(part) {
    const fs = faces(part, { flat: true });
    const xs = [], zs = [];
    fs.forEach(f => f.corners.forEach(p => { xs.push(p[0]); zs.push(p[2]); }));
    const length = Math.max(...xs) - Math.min(...xs), width = Math.max(...zs) - Math.min(...zs);
    const area = fs.reduce((t, f) => t + f.a * f.b, 0);
    /* folds: Flash Fold's rule - a fold counts 1, a hem (folded right back) is a crush fold and counts 2 */
    const folds = part.flanges.reduce((t, f) => t + (num(f.angle) >= 170 ? 2 : num(f.angle) > 0.5 ? 1 : 0), 0);
    return { length: +length.toFixed(1), width: +width.toFixed(1), area: +area.toFixed(0), folds, faces: fs.length };
  }

  /* what the aluminium engine prices: every part's blank, and the welds that are on */
  function forEngine(model) {
    const on = welds(model).filter(x => x.on), weldedParts = new Set();
    on.forEach(x => { weldedParts.add(x.a.part); weldedParts.add(x.b.part); });
    /* a part with a weld on it carries the welding cost in its rate (as a corner soaker does) */
    /* identical legs of one welded corner (same profile group, same blank, same quantity) are ONE item of n legs - exactly
       how a welded corner soaker is priced (2 legs, one rate each), so the same job prices the same however it is entered */
    const parts = [], merged = {};
    (model.parts || []).forEach((p, i) => { const b = blank(p), qty = p.qty || 1;
      const key = p.group && weldedParts.has(p.id) ? [p.group, b.width, b.folds, b.length, qty].join("|") : null;
      if (key && merged[key]) { merged[key].perItem++; return; }
      const it = { kind: "part", name: p.name || ("Part " + (i + 1)), girth: b.width, folds: b.folds, len: b.length, qty, perItem: 1, welded: weldedParts.has(p.id) };
      if (key) merged[key] = it; parts.push(it); });
    const w = on.map(x => ({ len: x.exact, qty: x.qty }));
    return { parts, welds: w };
  }

  /* ---------- editing ---------- */
  const clone = m => JSON.parse(JSON.stringify(m));
  const partOf = (m, id) => (m.parts || []).find(p => p.id === id);
  const groupOf = (m, part) => part.group ? m.parts.filter(p => p.group === part.group) : [part];
  /* the free edges of a face (where a flange can go) */
  function freeEdges(part, faceKey) {
    const fs = faces(part), f = fs.find(x => x.key === faceKey); if (!f) return [];
    const used = new Set(part.flanges.filter(x => (x.on || "base") === faceKey).map(x => Math.round(num(x.edge))));
    return [0, 1, 2, 3].filter(i => !(i === 0 && f.parent) && !used.has(i));
  }
  /* ⚠️ Charl, 19 Oct 08:38: "if you click on an edge and you say that you want the 90 degree turn up, then it only needs to
     happen on that edge ... currently if you select turn up, then it turns both of the ends up." A fold goes on the edge
     that was picked, on that leg only. `all` (asked for on purpose) folds the same edge on every leg of the frame. */
  function addFlange(model, partId, faceKey, edge, width, angle, dir, all) {
    const m = clone(model), part = partOf(m, partId); if (!part) return m;
    if (freeEdges(part, faceKey).indexOf(edge) < 0) return m;
    const f = { id: uid("f"), on: faceKey, edge, width: Math.max(1, num(width) || 25), angle: Math.max(0, Math.min(180, num(angle))), dir: num(dir) === -1 ? -1 : 1 };
    (all === true ? groupOf(m, part) : [part]).forEach(p => { if (!p.flanges.some(x => x.on === f.on && Math.round(num(x.edge)) === edge)) p.flanges.push(Object.assign({}, f)); });
    return relink(m);
  }
  function setFlange(model, partId, flangeId, patch) {
    const m = clone(model), part = partOf(m, partId); if (!part) return m;
    groupOf(m, part).forEach(p => p.flanges.forEach(f => { if (f.id === flangeId) {
      if (patch.width !== undefined) f.width = Math.max(1, num(patch.width));
      if (patch.angle !== undefined) f.angle = Math.max(0, Math.min(180, num(patch.angle)));
      if (patch.dir !== undefined) f.dir = num(patch.dir) === -1 ? -1 : 1; } }));
    return relink(m);
  }
  function removeFlange(model, partId, flangeId) {
    const m = clone(model), part = partOf(m, partId); if (!part) return m;
    groupOf(m, part).forEach(p => { let gone = new Set([flangeId]), grew = true;
      while (grew) { grew = false; p.flanges.forEach(f => { if (!gone.has(f.id) && gone.has(f.on)) { gone.add(f.id); grew = true; } }); }
      p.flanges = p.flanges.filter(f => !gone.has(f.id)); });
    return relink(m);
  }
  function setPart(model, partId, patch) {
    const m = clone(model), part = partOf(m, partId); if (!part) return m;
    if (patch.name !== undefined) part.name = String(patch.name).slice(0, 60);
    if (patch.qty !== undefined) part.qty = Math.max(1, Math.min(9999, Math.round(num(patch.qty) || 1)));
    if (patch.L !== undefined) part.L = Math.max(10, num(patch.L));
    if (patch.W !== undefined) groupOf(m, part).forEach(p => { p.W = Math.max(1, num(patch.W)); });
    return relink(m);
  }
  function addPart(model, o) {
    const m = clone(model);
    const xs = []; (m.parts || []).forEach(p => worldFaces({ parts: [p] }).forEach(f => f.corners.forEach(c => xs.push(c[2]))));
    const z = xs.length ? Math.max(...xs) + 150 : 0;
    m.parts.push(newPart(Object.assign({ name: "Part " + (m.parts.length + 1), pos: [0, 0, z] }, o)));
    return m;
  }
  function removePart(model, partId) {
    const m = clone(model); m.parts = m.parts.filter(p => p.id !== partId);
    m.parts.forEach(p => { ["start", "end"].forEach(k => { if (p.ends[k].to === partId) p.ends[k] = { turn: 0, side: 1 }; }); });
    return m;
  }
  /* "Mitred corner": at the END of a part, turn left (side 1) or right (side -1) by `angle` degrees and carry on with a new
     part of the same profile, mitred to match - a picture frame corner. Both legs then share their profile. */
  function addCorner(model, partId, side, angle, length) {
    const m = clone(model), A = partOf(m, partId); if (!A) return m;
    if (A.ends.end.to) return m;
    const phi = Math.max(1, Math.min(179, num(angle) || 90)), s = num(side) === -1 ? -1 : 1;
    if (!A.group) A.group = uid("g");
    A.ends.end = { turn: phi, side: s, to: null };
    const cut = mitreCut(A, rawFaces(A, false));
    const z0 = s === 1 ? cut.zmin : cut.zmax;
    const B = newPart({ name: "Part " + (m.parts.length + 1), group: A.group, L: Math.max(10, num(length) || A.L), W: A.W, qty: A.qty, flanges: clone(A.flanges),
      rotY: num(A.rotY) - s * phi, ends: { start: { turn: phi, side: s, to: A.id }, end: { turn: 0, side: 1 } } });
    B.pos = cleanP(sub(toWorld(A, [A.L, 0, z0]), rotY([0, 0, z0], B.rotY)));
    A.ends.end.to = B.id;
    m.parts.push(B);
    return m;
  }
  /* after a size changes, every part joined at a corner is put back on its corner */
  function relink(m) {
    let guard = 0; const seen = new Set();
    const walk = A => { const e = A.ends.end; if (!e.to || guard++ > 200 || seen.has(A.id)) return; seen.add(A.id); const B = partOf(m, e.to); if (!B || B.ends.start.closed) return;
      const cut = mitreCut(A, rawFaces(A, false)), z0 = num(e.side) === -1 ? cut.zmax : cut.zmin;
      B.rotY = num(A.rotY) - (num(e.side) === -1 ? -1 : 1) * num(e.turn);
      B.pos = cleanP(sub(toWorld(A, [A.L, 0, z0]), rotY([0, 0, z0], B.rotY))); walk(B); };
    m.parts.filter(p => !p.ends.start.to || p.ends.start.closed).forEach(walk);
    return m;
  }
  /* ⚠️ 17 Oct: "Close the frame" - the last leg joins the first with a mitred, welded corner, like the last corner of a
     picture frame. Only when the last leg really reaches the first; otherwise it says how far off it is. */
  function frameOf(m, part) {
    let root = part, guard = 0; while (root.ends.start.to && !root.ends.start.closed && guard++ < 200) { const r = partOf(m, root.ends.start.to); if (!r) break; root = r; }
    let last = root; guard = 0; while (last.ends.end.to && guard++ < 200) { const n = partOf(m, last.ends.end.to); if (!n || n === root) break; last = n; }
    return { root, last };
  }
  function closeCheck(model, partId) {
    const m = clone(model), P = partOf(m, partId); if (!P) return { ok: false, why: "Pick a part of the frame." };
    const { root, last } = frameOf(m, P);
    if (root === last || last.ends.end.to || root.ends.start.to) return { ok: false, why: "Turn at least two corners first - then the last leg can be joined to the first." };
    const dA = rotY([1, 0, 0], last.rotY), dF = rotY([1, 0, 0], root.rotY);
    let turn = Math.acos(Math.max(-1, Math.min(1, dot(dA, dF)))) * 180 / Math.PI;
    if (turn > 179) return { ok: false, why: "Add the last leg first (one more corner) - then close the frame." };
    if (turn < 1) return { ok: false, why: "The last leg runs the same way as the first - it cannot be closed with one corner." };
    /* which way: the same rule addCorner uses (a turn to side 1 takes the next leg to rotY - turn) */
    const d1 = rotY([1, 0, 0], last.rotY - turn), side = dot(d1, dF) > 0.999 ? 1 : -1;
    const cut = mitreCut(last, rawFaces(last, false)), z0 = side === 1 ? cut.zmin : cut.zmax;
    const pA = toWorld(last, [last.L, 0, z0]), pF = toWorld(root, [0, 0, z0]);
    const gap = len(sub(pF, pA)), need = last.L + dot(sub(pF, pA), dA);
    return { ok: gap < 1, gap: +gap.toFixed(1), need: +need.toFixed(1), turn: +turn.toFixed(2), side, root: root.id, last: last.id,
      why: gap < 1 ? "" : "The last leg ends " + Math.round(gap) + " mm from the start of the first" + (Math.abs(need - last.L) > 0.5 ? " - make " + last.name + " " + Math.round(need) + " mm long" : "") + ", and check the opposite legs match." };
  }
  function closeFrame(model, partId) {
    const c = closeCheck(model, partId); if (!c.ok) return model;
    const m = clone(model), A = partOf(m, c.last), F = partOf(m, c.root);
    A.ends.end = { turn: c.turn, side: c.side, to: F.id };
    F.ends.start = { turn: c.turn, side: c.side, to: A.id, closed: true };
    return m;
  }
  function moveCorner(model, partId, patch) {   /* change a corner's angle or which way it turns */
    const m = clone(model), A = partOf(m, partId); if (!A || !A.ends.end.to) return m;
    const B = partOf(m, A.ends.end.to);
    if (patch.turn !== undefined) { A.ends.end.turn = Math.max(1, Math.min(179, num(patch.turn))); if (B) B.ends.start.turn = A.ends.end.turn; }
    if (patch.side !== undefined) { A.ends.end.side = num(patch.side) === -1 ? -1 : 1; if (B) B.ends.start.side = A.ends.end.side; }
    return relink(m);
  }
  function toggleWeld(model, id) {
    const m = clone(model), s = new Set(m.weldsOff || []); if (s.has(id)) s.delete(id); else s.add(id); m.weldsOff = [...s]; return m;
  }
  /* a model sent from a phone is checked, never trusted */
  function sanitize(input) {
    const m = input && typeof input === "object" ? input : {};
    const parts = (Array.isArray(m.parts) ? m.parts : []).slice(0, 60).map(p => {
      const np = newPart({ id: String(p && p.id || uid("p")).slice(0, 40), name: String(p && p.name || "Part").replace(/[<>&"]/g, "").slice(0, 60), group: p && p.group ? String(p.group).slice(0, 40) : null,
        L: Math.min(20000, Math.max(10, num(p && p.L))), W: Math.min(3000, Math.max(1, num(p && p.W))), qty: Math.min(9999, Math.max(1, Math.round(num(p && p.qty) || 1))),
        pos: Array.isArray(p && p.pos) ? [0, 1, 2].map(i => Math.max(-1e6, Math.min(1e6, num(p.pos[i])))) : [0, 0, 0], rotY: num(p && p.rotY) % 360 });
      np.flanges = (Array.isArray(p && p.flanges) ? p.flanges : []).slice(0, 60).map(f => ({ id: String(f && f.id || uid("f")).slice(0, 40), on: String(f && f.on || "base").slice(0, 40),
        edge: Math.max(0, Math.min(3, Math.round(num(f && f.edge)))), width: Math.min(3000, Math.max(1, num(f && f.width))), angle: Math.max(0, Math.min(180, num(f && f.angle))), dir: num(f && f.dir) === -1 ? -1 : 1 }));
      ["start", "end"].forEach(k => { const e = (p && p.ends && p.ends[k]) || {}; np.ends[k] = { turn: Math.max(0, Math.min(179, num(e.turn))), side: num(e.side) === -1 ? -1 : 1, to: e.to ? String(e.to).slice(0, 40) : null }; if (k === "start" && e.closed === true) np.ends.start.closed = true; });
      const c = cleanCoil(p && p.coil); if (c) np.coil = c;
      return np;
    });
    return { v: 1, parts, weldsOff: (Array.isArray(m.weldsOff) ? m.weldsOff : []).slice(0, 400).map(x => String(x).slice(0, 200)) };
  }
  /* ⚠️ Charl, 17 Oct 05:53: "how do I draw on what you've built ... you can't actually continue drawing" - the end profile is
     DRAWN like a coil flashing (points clicked in the end view) and becomes a part: its longest leg is the base plate, every
     other leg a flange folded off the one before it, at the angle drawn, up or down as drawn.
     pts: [[across, up], ...] in mm. Returns { W, flanges } for a part. */
  function profileFromSketch(pts) {
    const P = (pts || []).map(q => [num(q[0]), num(q[1])]);
    const legs = []; for (let i = 0; i < P.length - 1; i++) { const dx = P[i + 1][0] - P[i][0], dy = P[i + 1][1] - P[i][1], L = Math.hypot(dx, dy); if (L > 0.5) legs.push({ a: P[i], b: P[i + 1], L, d: [dx / L, dy / L] }); }
    if (!legs.length) return null;
    let bi = 0; legs.forEach((g, i) => { if (g.L > legs[bi].L + 1e-9) bi = i; });
    const base = legs[bi], up0 = [-base.d[1], base.d[0]];
    const flanges = [];
    const fold = (v, up, w) => { const c = Math.max(-1, Math.min(1, v[0] * w[0] + v[1] * w[1])), th = Math.acos(c), sgn = (up[0] * w[0] + up[1] * w[1]) >= 0 ? 1 : -1;
      return { th, sgn, v2: w, up2: [up[0] * Math.cos(th) - sgn * v[0] * Math.sin(th), up[1] * Math.cos(th) - sgn * v[1] * Math.sin(th)] }; };
    const chain = (list, startEdge, v0) => { let on = "base", edge = startEdge, v = v0, up = up0;
      list.forEach(g => { const f = fold(v, up, g.d), id = uid("f");
        flanges.push({ id, on, edge, width: +g.L.toFixed(1), angle: +(f.th * 180 / Math.PI).toFixed(2), dir: f.sgn });
        on = id; edge = 2; v = f.v2; up = f.up2; }); };
    chain(legs.slice(bi + 1), 2, base.d);                                                        /* after the base: off its back edge */
    chain(legs.slice(0, bi).reverse().map(g => ({ L: g.L, d: [-g.d[0], -g.d[1]] })), 0, [-base.d[0], -base.d[1]]);   /* before it: off its front edge */
    return { W: +base.L.toFixed(1), flanges };
  }
  function setProfile(model, partId, prof, coil) {
    const m = clone(model), part = partOf(m, partId); if (!part || !prof) return m;
    groupOf(m, part).forEach(p => { p.W = prof.W; p.flanges = prof.flanges.map(f => Object.assign({}, f)); delete p.coil; });
    const r = relink(m);
    /* the coil drawing it came from is kept, so ✎ Profile opens exactly what was drawn - until the profile is changed in 3D */
    if (coil) { const c = cleanCoil(coil); if (c) groupOf(r, partOf(r, partId)).forEach(p => { p.coil = Object.assign({}, c, { sig: profSig(p) }); }); }
    return r;
  }
  /* ⚠️ Charl, 17 Oct 06:22: "when you draw the piece can it not be as simple as our other canvas where points can be dragged - I
     actually want all those functions that we have in the original canvas. That way the customer doesn't have to learn new
     ways of doing things." The end profile is drawn on the COIL canvas itself and comes here as its points:
     pts [[x, y], ...] in mm with y DOWN (as the coil canvas draws), hem { start, end } each null or { fold, flip } - the metal
     in the return (mm) and the side the coil canvas folds it to. A hem is a leg folded back flat (counts 2 folds). */
  function profileFromCoil(pts, hem) {
    const P = (pts || []).map(q => [num(q[0]), num(q[1])]); if (P.length < 2) return null;
    const ret = (E, N, h) => { const dx = N[0] - E[0], dy = N[1] - E[1], l = Math.hypot(dx, dy) || 1, lx = dx / l, ly = dy / l, f = num(h.flip) === -1 ? -1 : 1, F = Math.max(1, num(h.fold));
      return [E[0] + lx * F - ly * f * 0.02, E[1] + ly * F + lx * f * 0.02]; };   /* back along the leg, on the side the coil canvas draws it */
    const Q = P.slice(), H = hem || {};
    if (H.start) Q.unshift(ret(P[0], P[1], H.start));
    if (H.end) Q.push(ret(P[P.length - 1], P[P.length - 2], H.end));
    return profileFromSketch(Q.map(q => [q[0], -q[1]]));
  }
  function profSig(part) { return [num(part.W).toFixed(1)].concat((part.flanges || []).map(f => [f.on === "base" ? "b" : "f", f.edge, num(f.width).toFixed(1), num(f.angle).toFixed(2), f.dir].join(","))).join(";"); }
  function cleanCoil(c) {
    if (!c || !Array.isArray(c.pts) || c.pts.length < 2) return null;
    const pts = c.pts.slice(0, 80).map(q => [Math.max(-1e5, Math.min(1e5, num(q && q[0]))), Math.max(-1e5, Math.min(1e5, num(q && q[1])))]);
    const h = c.hems && typeof c.hems === "object" ? c.hems : {}, id = v => (/^[a-z0-9]{1,20}$/i.test(String(v || "")) ? String(v) : "none");
    const hems = { start: id(h.start), end: id(h.end), startFlip: num(h.startFlip) === -1 ? -1 : 1, endFlip: num(h.endFlip) === -1 ? -1 : 1,
      startFoldMm: Math.max(0, Math.min(500, num(h.startFoldMm) || 10)), endFoldMm: Math.max(0, Math.min(500, num(h.endFoldMm) || 10)) };
    return { pts, hems, sig: String(c.sig || "").slice(0, 4000) };
  }
  /* what to put on the coil canvas to change a part's profile: the drawing it came from, or (changed in 3D since) its end view */
  function coilOf(part) {
    if (!part) return { pts: [], hems: null };
    if (part.coil && part.coil.sig === profSig(part)) return { pts: part.coil.pts.map(q => q.slice()), hems: Object.assign({}, part.coil.hems) };
    return { pts: sketchOf(part).map(q => [q[0], -q[1]]), hems: null };
  }
  /* the part's end profile as drawn points (across, up) - to show and edit it in the end view again */
  function sketchOf(part) {
    const fs = faces(part, {});
    const base = fs.find(f => f.key === "base"); if (!base) return [];
    const pt = p => [+p[2].toFixed(1), +p[1].toFixed(1)];                      /* across = z, up = y, at x = 0 */
    const after = [], before = [];
    let on = "base"; while (true) { const f = part.flanges.find(x => (x.on || "base") === on && Math.round(num(x.edge)) === (on === "base" ? 2 : 2)); if (!f) break; const F = fs.find(x => x.key === f.id); if (!F) break; after.push(pt(F.corners[3])); on = f.id; }
    on = "base"; let first = true; while (true) { const f = part.flanges.find(x => (x.on || "base") === on && Math.round(num(x.edge)) === (first ? 0 : 2)); if (!f) break; const F = fs.find(x => x.key === f.id); if (!F) break; before.push(pt(first ? F.corners[2] : F.corners[2])); on = f.id; first = false; }
    return before.reverse().concat([pt(base.corners[0]), pt(base.corners[3])], after);
  }
  /* ⚠️ 17 Oct: the floor folds from the END PROFILE - every leg's length and every bend's inside angle, drawn the way the
     coil job sheet draws a flashing. A plain SVG string, for the job sheet and the quote. */
  function profileSvg(part, opts) {
    const o = opts || {}, W = o.width || 240, H = o.height || 160, pad = 26;
    const pts = sketchOf(part); if (pts.length < 2) return "";
    const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const k = Math.min((W - 2 * pad) / Math.max(1, maxX - minX), (H - 2 * pad) / Math.max(1, maxY - minY));
    const ox = (W - (maxX - minX) * k) / 2, oy = (H - (maxY - minY) * k) / 2;
    const X = x => +(ox + (x - minX) * k).toFixed(1), Y = y => +(H - oy - (y - minY) * k).toFixed(1);
    const esc = t => String(t).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    let out = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" data-profile="1"><rect width="${W}" height="${H}" fill="#ffffff"/>`;
    out += `<polyline points="${pts.map(p => X(p[0]) + "," + Y(p[1])).join(" ")}" fill="none" stroke="#211326" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>`;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1], L = Math.hypot(b[0] - a[0], b[1] - a[1]); if (L < 0.5) continue;
      const mx = (X(a[0]) + X(b[0])) / 2, my = (Y(a[1]) + Y(b[1])) / 2, nx = -(Y(b[1]) - Y(a[1])), ny = X(b[0]) - X(a[0]), nl = Math.hypot(nx, ny) || 1;
      out += `<text x="${(mx + nx / nl * 11).toFixed(1)}" y="${(my + ny / nl * 11 + 4).toFixed(1)}" text-anchor="middle" font-family="Arial" font-size="12" font-weight="700" fill="#834192">${esc(Math.round(L * 10) / 10)}</text>`;
    }
    for (let i = 1; i < pts.length - 1; i++) {
      const a = pts[i - 1], b = pts[i], c = pts[i + 1], v1 = [a[0] - b[0], a[1] - b[1]], v2 = [c[0] - b[0], c[1] - b[1]];
      const ang = Math.acos(Math.max(-1, Math.min(1, (v1[0] * v2[0] + v1[1] * v2[1]) / ((Math.hypot(...v1) || 1) * (Math.hypot(...v2) || 1))))) * 180 / Math.PI;
      out += `<text x="${X(b[0]) + 5}" y="${Y(b[1]) + 14}" font-family="Arial" font-size="10.5" font-weight="700" fill="#e05c00">${Math.round(ang)}°</text>`;
    }
    out += `<circle cx="${X(pts[0][0])}" cy="${Y(pts[0][1])}" r="3" fill="#16a34a"/></svg>`;
    return out;
  }
  return { closeCheck, closeFrame, profileSvg, profileFromSketch, profileFromCoil, coilOf, profSig, setProfile, sketchOf, newModel, newPart, faces, worldFaces, welds, blank, forEngine, freeEdges, addFlange, setFlange, removeFlange, setPart, addPart, removePart, addCorner, moveCorner, relink, toggleWeld, sanitize, toWorld, rotY, num };
});
